import {
  Banner,
  Button,
  DataTable,
  DefinitionList,
  DetailLayout,
  Dialog,
  ErrorState,
  PageHeader,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  StatusPill,
  TabPanel,
  Tabs,
  TextAreaField,
  TextField,
  Toggle,
  Truncate,
  notify,
  statusBadge,
  Badge,
  useConfirm,
  type Column,
} from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { shortDate, usd } from "./CustomersList";
import type { Ticket } from "../service/Tickets";
import { AddUserDialog, EmailNotConnected, UserActions, useEmailStatus } from "./Onboarding";

type Money = { cents: number; currency: "USD" };
export type CustomerDetailData = {
  customer: { id: string; name: string; prospect: boolean; phone?: string | null; prospectContact: { name?: string; email?: string; phone?: string } | null; createdAt: string; creditTermsDays: number | null; creditLimit: Money | null; zohoContactId?: string | null };
  /** The customer's default markup (percent over carrier cost, basis points); null = the house default. Cost readers only. */
  defaultMarkup?: { pctBps: number | null; houseDefaultBps: number; ruleId: string | null } | null;
  totals: { shipments: number; revenue: Money; margin?: Money; openInvoices: number; openBalance: Money; overdueInvoices: number };
  users: { id: string; name: string | null; email: string; role: string; mfa: boolean; disabled: boolean; invitePending: boolean; joinedAt: string; lastSeen: string | null; otherCustomers?: number }[];
  quotes: { id: string; status: string; createdAt: string; originPostal: string; destinationPostal: string; lowest: Money | null }[];
  shipments: { id: string; status: string; carrier: string; bolNumber: string | null; pickupDate: string | null; createdAt: string; price: Money; margin?: Money }[];
  markup: { id: string; name: string; kind: string; active: boolean; priority: number }[];
  carriers: { carrier: string; label: string | null; mode: string; enabled: boolean }[] | null;
  notes: { id: string; body: string; createdAt: string; author: string | null }[];
  timeline: { at: string; kind: string; text: string }[];
  showsCost: boolean;
};

const TABS = ["overview", "users", "quotes", "shipments", "invoices", "tickets", "pricing", "notes"] as const;
type Tab = (typeof TABS)[number];
const TAB_ID = "customer";
const money = (m: Money) => usd.format(m.cents / 100);
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const terms = (days: number | null) => (days ? `Net ${days}` : "Prepay");

/** Customers > one account: DetailLayout with the account facts beside the tabs (overview, users, quotes, ...). */
export function CustomerDetail() {
  const { orgId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? "") ? (raw as Tab) : "overview";
  const q = useQuery({ queryKey: ["console", "customer", orgId], queryFn: () => api<CustomerDetailData>(`/v1/console/customers/${orgId}`) });
  const me = useConsoleMe();
  const [viewing, setViewing] = useState(false);
  const canTickets = me.capabilities.includes("tickets:read");
  const canBilling = me.capabilities.includes("billing:read");
  const invoices = useQuery({ queryKey: ["console", "customer", orgId, "invoices"], queryFn: () => api<{ invoices: Invoice[] }>(`/v1/console/customers/${orgId}/invoices`), enabled: canBilling && !!orgId });
  const tickets = useQuery({
    queryKey: ["console", "customer", orgId, "tickets"],
    queryFn: () => api<{ tickets: Ticket[]; hasMore: boolean }>(`/v1/console/tickets?${new URLSearchParams({ view: "all", orgId, page: "0" })}`),
    enabled: canTickets && !!orgId,
  });
  const back = (
    <Link to="/customers" className="ffa-back">
      ← Customers
    </Link>
  );

  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout
          header={<PageHeader back={back} eyebrow="Customer" title={<Skeleton width="14rem" height="2.25rem" />} subtitle={<Skeleton width="18rem" />} />}
          aside={<Section title="Account"><SkeletonText lines={5} /></Section>}
        >
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading customer…</span>
      </section>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Customer not found" : "Customer"} />
        {missing ? <Banner tone="warning" title="This customer doesn't exist.">It may have been removed, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this customer" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const d = q.data;
  const c = d.customer;
  const kind = statusBadge("customerKind", "prospect");
  const setTab = (t: Tab) => setParams((p) => { const n = new URLSearchParams(p); if (t === "overview") n.delete("tab"); else n.set("tab", t); return n; }, { replace: true });

  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="Customer"
            title={c.name}
            badges={c.prospect ? <Badge tone={kind.tone} dot={false}>{kind.label}</Badge> : null}
            subtitle={`Customer since ${shortDate(c.createdAt)} · ${terms(c.creditTermsDays)}${c.creditLimit ? ` · ${money(c.creditLimit)} credit limit` : ""}`}
            secondaryActions={me.capabilities.includes("customers:impersonate") && d.users.some((u) => !u.disabled) ? <Button onClick={() => setViewing(true)}>View as customer</Button> : null}
            primaryAction={<Link className="ffx-btn ffx-btn--primary" to={`/quotes/new?${new URLSearchParams({ org: c.id, name: c.name })}`}>New quote</Link>}
          />
        }
        notices={<ActiveViews orgId={c.id} />}
        summary={
          <div className="ffa-kpis">
            <Kpi label="Shipments" value={String(d.totals.shipments)} />
            <Kpi label="Revenue" value={money(d.totals.revenue)} />
            {d.totals.margin ? <Kpi label="Margin" value={money(d.totals.margin)} /> : null}
            <Kpi label="Open balance" value={money(d.totals.openBalance)} sub={d.totals.overdueInvoices ? `${d.totals.overdueInvoices} overdue` : `${d.totals.openInvoices} open invoices`} warn={d.totals.overdueInvoices > 0} />
          </div>
        }
        tabs={
          <Tabs
            idBase={TAB_ID}
            label="Customer sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "overview", label: "Overview" },
              { value: "users", label: "Users", count: d.users.length },
              { value: "quotes", label: "Quotes", count: d.quotes.length },
              { value: "shipments", label: "Shipments", count: d.shipments.length },
              ...(canBilling ? [{ value: "invoices" as const, label: "Invoices", ...(invoices.data ? { count: invoices.data.invoices.length } : {}) }] : []),
              ...(canTickets ? [{ value: "tickets" as const, label: "Tickets", ...(tickets.data ? { count: tickets.data.tickets.length } : {}) }] : []),
              { value: "pricing", label: "Pricing" },
              { value: "notes", label: "Notes", count: d.notes.length },
            ]}
          />
        }
        aside={<AccountPanel d={d} />}
        asideLabel="Account"
      >
        <TabPanel idBase={TAB_ID} value={tab}>
          {tab === "overview" ? <Overview d={d} /> : null}
          {tab === "users" ? <Users d={d} canWrite={me.capabilities.includes("customers:write")} /> : null}
          {tab === "quotes" ? <Quotes d={d} /> : null}
          {tab === "shipments" ? <Shipments d={d} /> : null}
          {tab === "invoices" && canBilling ? <CustomerInvoices q={invoices} /> : null}
          {tab === "tickets" && canTickets ? <CustomerTickets q={tickets} /> : null}
          {tab === "pricing" ? <Pricing d={d} /> : null}
          {tab === "notes" ? <Notes d={d} orgId={c.id} /> : null}
        </TabPanel>
      </DetailLayout>
      {viewing ? <ViewAsDialog d={d} onClose={() => setViewing(false)} /> : null}
    </section>
  );
}

function Kpi({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <section className="ffx-card ffa-kpi" aria-label={label}>
      <span className="ffx-micro">{label}</span>
      <span className="ffa-kpi__value ffx-num">{value}</span>
      {sub ? <span className={warn ? "ffa-kpi__sub ffa-warn" : "ffa-kpi__sub ffa-muted"}>{sub}</span> : null}
    </section>
  );
}

/** Side panel: the facts staff look up while on any tab. */
function AccountPanel({ d }: { d: CustomerDetailData }) {
  const c = d.customer;
  const contact = c.prospectContact;
  const activeRules = d.markup.filter((m) => m.active).length;
  return (
    <Section title="Account">
      <DefinitionList
        items={[
          { term: "Type", detail: c.prospect ? "Prospect" : "Customer" },
          { term: "Terms", detail: terms(c.creditTermsDays) },
          c.creditLimit ? { term: "Credit limit", detail: money(c.creditLimit) } : null,
          { term: "Users", detail: d.users.length },
          c.phone ? { term: "Phone", detail: <a href={`tel:${c.phone}`}>{c.phone}</a> } : null,
          { term: "Carrier accounts", detail: d.carriers ? d.carriers.map((x) => x.label ?? x.carrier).join(", ") : "Uses FlexFreight rates" },
          { term: "Pricing rules", detail: activeRules ? `${activeRules} active` : "Default markup" },
          contact ? { term: "Contact", detail: [contact.name, contact.email, contact.phone].filter(Boolean).join(" · ") } : null,
          { term: "Customer since", detail: shortDate(c.createdAt) },
        ]}
      />
    </Section>
  );
}

function Overview({ d }: { d: CustomerDetailData }) {
  return (
    <Section title="Recent activity">
      {d.timeline.length === 0 ? (
        <p className="ffa-muted">No activity yet.</p>
      ) : (
        <ol className="ffa-timeline">
          {d.timeline.map((e, i) => (
            <li key={`${e.at}-${i}`} data-kind={e.kind}>
              <time dateTime={e.at} className="ffa-muted">{shortDate(e.at)}</time>
              <span>{e.text}</span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

type User = CustomerDetailData["users"][number];
const signin = (u: User) => (u.disabled ? "disabled" : u.invitePending ? "invite_pending" : u.mfa ? "mfa" : "password");

function Users({ d, canWrite }: { d: CustomerDetailData; canWrite: boolean }) {
  const email = useEmailStatus();
  const [adding, setAdding] = useState(false);
  const columns: Column<User>[] = [
    { key: "name", header: "Name", primary: true, render: (u) => (<><span className="ffa-cell-strong">{u.name ?? u.email}</span><span className="ffa-muted ffa-block">{u.email}</span></>) },
    { key: "role", header: "Role", width: "7rem", render: (u) => <span className="ffa-cap">{u.role}</span> },
    { key: "signin", header: "Sign-in", width: "9rem", render: (u) => <StatusBadge domain="userSignin" value={signin(u)} /> },
    { key: "seen", header: "Last seen", width: "8.5rem", render: (u) => (u.lastSeen ? <time dateTime={u.lastSeen}>{shortDate(u.lastSeen)}</time> : <span className="ffa-muted">Never</span>) },
    ...(canWrite ? [{ key: "actions", header: "Actions", render: (u: User) => <UserActions orgId={d.customer.id} user={u} email={email.data} /> }] : []),
  ];
  return (
    <Section
      title="Users"
      description="People who sign in to the portal for this account."
      actions={canWrite ? <Button variant="primary" size="sm" onClick={() => setAdding(true)}>Add user</Button> : null}
      flush
    >
      {canWrite ? <div className="ffa-sectionnote"><EmailNotConnected status={email.data} /></div> : null}
      {adding ? <AddUserDialog orgId={d.customer.id} orgName={d.customer.name} onClose={() => setAdding(false)} /> : null}
      <DataTable caption="Users" columns={columns} rows={d.users} rowKey={(u) => u.id} empty="No users on this account." maxHeight="none" />
    </Section>
  );
}

type QuoteRow = CustomerDetailData["quotes"][number];
function Quotes({ d }: { d: CustomerDetailData }) {
  const navigate = useNavigate();
  const columns: Column<QuoteRow>[] = [
    { key: "created", header: "Created", primary: true, render: (x) => <time dateTime={x.createdAt}>{shortDate(x.createdAt)}</time> },
    { key: "lane", header: "Lane", render: (x) => <span className="ffx-num">{x.originPostal} → {x.destinationPostal}</span> },
    { key: "status", header: "Status", render: (x) => <StatusBadge domain="quote" value={x.status} /> },
    { key: "lowest", header: "Lowest", numeric: true, render: (x) => (x.lowest ? money(x.lowest) : "–") },
  ];
  const all = `/quotes?q=${encodeURIComponent(d.customer.name)}`;
  return (
    <Section title="Recent quotes" actions={<Link className="ffa-sectionlink" to={all}>All quotes for {d.customer.name}</Link>} flush>
      <DataTable caption="Recent quotes" columns={columns} rows={d.quotes} rowKey={(x) => x.id} empty="No quotes yet." maxHeight="none" onRowClick={() => navigate(all)} />
    </Section>
  );
}

type ShipRow = CustomerDetailData["shipments"][number];
function Shipments({ d }: { d: CustomerDetailData }) {
  const columns: Column<ShipRow>[] = [
    { key: "bol", header: "BOL", primary: true, render: (s) => <span className="ffx-num">{s.bolNumber ?? "Pending"}</span> },
    { key: "carrier", header: "Carrier", render: (s) => <span className="ffa-upper">{s.carrier}</span> },
    { key: "pickup", header: "Pickup", render: (s) => (s.pickupDate ? <time dateTime={s.pickupDate}>{shortDate(`${s.pickupDate}T12:00:00`)}</time> : "–") },
    { key: "status", header: "Status", render: (s) => <StatusBadge domain="shipment" value={s.status} /> },
    { key: "price", header: "Price", numeric: true, render: (s) => money(s.price) },
    ...(d.showsCost ? [{ key: "margin", header: "Margin", numeric: true, render: (s: ShipRow) => (s.margin ? money(s.margin) : "–") }] : []),
  ];
  return (
    <Section title="Recent shipments" actions={<Link className="ffa-sectionlink" to={`/shipments?${new URLSearchParams({ org: d.customer.id, bucket: "all" })}`}>All shipments for {d.customer.name}</Link>} flush>
      <DataTable caption="Recent shipments" columns={columns} rows={d.shipments} rowKey={(s) => s.id} empty="No shipments yet." maxHeight="none" />
    </Section>
  );
}

function Pricing({ d }: { d: CustomerDetailData }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const canWrite = me.capabilities.includes("pricing:write");
  const dm = d.defaultMarkup ?? null;
  const [pct, setPct] = useState<string | null>(null);
  const shown = pct ?? (dm?.pctBps === null || dm?.pctBps === undefined ? "" : (dm.pctBps / 100).toString());
  const parsed = shown.trim() === "" ? null : Number(shown);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0 && parsed <= 500);
  const save = useMutation({
    mutationFn: () => api(`/v1/console/customers/${d.customer.id}/default-markup`, { method: "PUT", body: { pctBps: parsed === null ? null : Math.round(parsed * 100) } }),
    onSuccess: () => {
      setPct(null);
      void qc.invalidateQueries({ queryKey: ["console", "customer", d.customer.id] });
      notify.success(parsed === null ? "Default markup cleared" : `Default markup set to ${parsed}%`, "Quotes from now on price at it; invoices bill what the shipment was booked at.");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  return (
    <>
      {dm ? (
        <Section title="Default markup" description={`The percentage over carrier cost every quote (and so every auto-invoice) for this customer prices at, unless a more specific rule or a staff override applies. Blank = the house default (${dm.houseDefaultBps / 100}%).`}>
          {canWrite ? (
            <form
              className="ffa-inlineform"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) save.mutate();
              }}
            >
              <TextField label="Default markup (%)" inputMode="decimal" value={shown} onChange={(e) => setPct(e.target.value)} error={valid ? undefined : "0 to 500"} hint={dm.pctBps === null ? `House default ${dm.houseDefaultBps / 100}% applies` : undefined} />
              <Button type="submit" size="lg" variant="primary" disabled={!valid || pct === null} loading={save.isPending}>Save</Button>
            </form>
          ) : (
            <p className="ffa-small">{dm.pctBps === null ? `House default (${dm.houseDefaultBps / 100}%).` : `${dm.pctBps / 100}% over carrier cost.`}</p>
          )}
        </Section>
      ) : null}
    <Section title="Markup rules" description="These are the rules the rating engine applies to this customer's quotes, in order. With no active rule, the default markup applies.">
      {d.markup.length === 0 ? (
        <p className="ffa-muted">No customer-specific rules. Default markup applies.</p>
      ) : (
        <ul className="ffa-plainlist">
          {d.markup.map((m) => (
            <li key={m.id} className="ffa-rule">
              <span className="ffa-cell-strong">{m.name}</span> <span className="ffa-muted">({m.kind})</span> <StatusBadge domain="markupRule" value={m.active ? "active" : "off"} />
            </li>
          ))}
        </ul>
      )}
    </Section>
    </>
  );
}

function Notes({ d, orgId }: { d: CustomerDetailData; orgId: string }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const add = useMutation({
    mutationFn: () => api(`/v1/console/customers/${orgId}/notes`, { method: "POST", body: { body: body.trim() } }),
    onSuccess: () => {
      setBody("");
      void qc.invalidateQueries({ queryKey: ["console", "customer", orgId] });
      notify.success("Note added");
    },
    onError: (e) => notify.error("Couldn't add the note", message(e)),
  });
  const canWrite = me.capabilities.includes("customers:write");
  return (
    <div className="ffa-split">
      <Section title="Internal notes" description="Staff only. Customers never see these.">
        {d.notes.length === 0 ? (
          <p className="ffa-muted">No notes yet.</p>
        ) : (
          <ul className="ffa-notes">
            {d.notes.map((n) => (
              <li key={n.id}>
                <p>{n.body}</p>
                <span className="ffa-muted">{n.author ?? "Staff"} · <time dateTime={n.createdAt}>{shortDate(n.createdAt)}</time></span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {canWrite ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) add.mutate();
          }}
        >
          <Section title="Add a note">
            <TextAreaField label="Note" value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={4000} />
            <div>
              <Button type="submit" variant="primary" disabled={!body.trim()} loading={add.isPending}>Save note</Button>
            </div>
          </Section>
        </form>
      ) : null}
    </div>
  );
}

type ViewSession = { jti: string; staff: string | null; target: string; mode: "read" | "write"; reason: string; startedAt: string; expiresAt: string; mine: boolean };
const clock = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });

/** Open "view as customer" sessions on this account, with End. Everyone who can read the customer sees them. */
function ActiveViews({ orgId }: { orgId: string }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const q = useQuery({
    enabled: me.capabilities.includes("customers:impersonate"),
    queryKey: ["console", "view-as", orgId],
    queryFn: () => api<{ sessions: ViewSession[] }>(`/v1/console/customers/${orgId}/view-as`),
    refetchInterval: 30_000,
  });
  const end = useMutation({
    mutationFn: (jti: string) => api<{ ended: boolean }>(`/v1/console/view-as/${jti}/end`, { method: "POST", body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "view-as", orgId] });
      notify.success("Customer view ended", "That portal tab is signed out now.");
    },
    onError: (e) => notify.error("Couldn't end it", message(e)),
  });
  const rows = q.data?.sessions ?? [];
  if (rows.length === 0) return null;
  const canEndOthers = me.capabilities.includes("customers:act_as");
  return (
    <section className="ffx-banner ffx-banner--warning ffa-viewas" aria-label="Active customer views">
      <ul className="ffa-plainlist">
        {rows.map((s) => (
          <li key={s.jti} className="ffa-viewas__row">
            <StatusPill tone={s.mode === "write" ? "warning" : "info"}>{s.mode === "write" ? "Can make changes" : "View only"}</StatusPill>
            <span>
              <strong>{s.mine ? "You are" : `${s.staff ?? "Staff"} is`}</strong> viewing as {s.target} until {clock.format(new Date(s.expiresAt))}. <span className="ffa-muted">Reason: {s.reason}</span>
            </span>
            {s.mine || canEndOthers ? (
              <Button
                size="sm"
                variant="ghost"
                loading={end.isPending && end.variables === s.jti}
                onClick={async () => {
                  const ok = await confirm({ title: "End this customer view?", body: "The portal tab is signed out on its next click.", confirmLabel: "End view", cancelLabel: "Keep it" });
                  if (ok) end.mutate(s.jti);
                }}
              >
                End
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {dialog}
    </section>
  );
}

function ViewAsDialog({ d, onClose }: { d: CustomerDetailData; onClose: () => void }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const users = d.users.filter((u) => !u.disabled);
  const [userId, setUserId] = useState(users.find((u) => u.role === "owner")?.id ?? users[0]?.id ?? "");
  const [reason, setReason] = useState("");
  const [write, setWrite] = useState(false);
  const canWrite = me.capabilities.includes("customers:act_as");
  const start = useMutation({
    mutationFn: () =>
      api<{ jti: string; url: string; linkExpiresAt: string; expiresAt: string; mode: "read" | "write" }>(`/v1/console/customers/${d.customer.id}/view-as`, {
        method: "POST",
        body: { userId, reason: reason.trim(), mode: write ? "write" : "read" },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["console", "view-as", d.customer.id] }),
    onError: (e) => notify.error("Couldn't start the customer view", message(e)),
  });
  const valid = userId !== "" && reason.trim().length >= 10;
  const done = start.data;
  return (
    <Dialog
      open
      onClose={onClose}
      title={done ? "Customer view ready" : `View as ${d.customer.name}`}
      description={
        done
          ? `Open it within a minute; the link works once. The view ${done.mode === "write" ? "can make changes and" : "is view-only and"} ends at ${clock.format(new Date(done.expiresAt))}.`
          : "Opens the customer portal as this person in a new tab. It never uses their password, and every page and action is logged with your name and reason."
      }
      footer={
        done ? (
          <>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <a className="ffx-btn ffx-btn--primary" href={done.url} target="_blank" rel="noopener noreferrer" onClick={() => window.setTimeout(onClose, 0)}>
              Open customer portal
            </a>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!valid} loading={start.isPending} onClick={() => start.mutate()}>
              Start view
            </Button>
          </>
        )
      }
    >
      {done ? null : (
        <div className="ffa-form">
          <Select label="View as" value={userId} onChange={(v) => setUserId(v)} options={users.map((u) => ({ value: u.id, label: `${u.name ?? u.email} (${u.role})` }))} />
          <TextAreaField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} hint="Required, at least 10 characters. Saved in the customer's audit log." />
          {canWrite ? (
            <>
              <Toggle label="Allow changes" checked={write} onChange={setWrite} />
              <p className={write ? "ffa-warn ffa-small" : "ffa-muted ffa-small"}>
                {write ? "You can create and change things as this customer for 15 minutes. Use only when they asked you to." : "Off: view only for 30 minutes. Nothing can be changed."}
              </p>
            </>
          ) : (
            <p className="ffa-muted ffa-small">View only for 30 minutes. Making changes as a customer needs an owner or admin.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}

type TicketRow = Ticket;
function CustomerTickets({ q }: { q: { data?: { tickets: Ticket[]; hasMore: boolean }; isPending: boolean; isError: boolean; refetch?: () => unknown } }) {
  const columns: Column<TicketRow>[] = [
    { key: "ticket", header: "Ticket", primary: true, truncate: "custom", render: (t) => <Truncate text={`#${t.number} ${t.subject}`}><Link to={`/tickets/${t.id}`}>#{t.number} {t.subject}</Link></Truncate> },
    { key: "status", header: "Status", width: "11rem", render: (t) => <StatusBadge domain="ticket" value={t.status} /> },
    { key: "requester", header: "Requester", render: (t) => t.requesterName ?? t.requesterEmail ?? "–" },
    { key: "last", header: "Last activity", width: "9rem", render: (t) => shortDate(t.lastActivityAt) },
  ];
  return (
    <Section
      title="Support tickets"
      actions={q.data?.hasMore ? <Link className="ffa-sectionlink" to="/tickets?view=all">See all tickets</Link> : null}
      flush
    >
      <DataTable
        caption="Support tickets"
        columns={columns}
        rows={q.data?.tickets ?? []}
        rowKey={(t) => t.id}
        loading={q.isPending}
        error={q.isError ? { title: "Couldn't load tickets", onRetry: q.refetch ? () => void q.refetch!() : undefined } : null}
        empty="No support tickets for this customer."
        maxHeight="none"
      />
    </Section>
  );
}

type Invoice = {
  id: string;
  number: string;
  zohoNumber: string | null;
  shipmentId: string;
  bolNumber: string | null;
  issuedAt: string;
  dueDate: string;
  amount: Money;
  status: "open" | "overdue" | "paid" | "void";
  paidAt: string | null;
  issuedOn?: "booked" | "delivered";
  emailedAt?: string | null;
  pdfDocumentId?: string | null;
  zoho?: { status: "synced" | "failed" | "not_synced"; lastError: string | null };
};

function CustomerInvoices({ q }: { q: { data?: { invoices: Invoice[] }; isPending: boolean; isError: boolean; refetch?: () => unknown } }) {
  const columns: Column<Invoice>[] = [
    { key: "number", header: "Invoice", primary: true, render: (i) => (<><span className="ffx-num">{i.number}</span>{i.zohoNumber ? <span className="ffa-muted ffa-block ffa-small">Zoho {i.zohoNumber}</span> : null}</>) },
    { key: "bol", header: "Shipment", render: (i) => (i.bolNumber ? <Link to={`/shipments?bucket=all&q=${encodeURIComponent(i.bolNumber)}`}>{i.bolNumber}</Link> : "–") },
    { key: "issued", header: "Issued", render: (i) => shortDate(i.issuedAt), sub: (i) => (i.issuedOn === "booked" ? "at booking" : i.issuedOn === "delivered" ? "on delivery" : null) },
    { key: "due", header: "Due", render: (i) => shortDate(`${i.dueDate}T12:00:00Z`) },
    { key: "amount", header: "Amount", numeric: true, render: (i) => money(i.amount) },
    { key: "status", header: "Status", width: "8rem", render: (i) => <StatusBadge domain="invoice" value={i.status} /> },
    {
      key: "zoho",
      header: "Zoho Books",
      width: "11rem",
      render: (i) =>
        i.zoho?.status === "failed" ? (
          <span className="ffa-small ffa-warntext" title={i.zoho.lastError ?? undefined}>Sync failed</span>
        ) : (
          <span className="ffx-cellstack">
            <span className="ffa-small">{i.emailedAt ? `Emailed ${shortDate(i.emailedAt)}` : i.zoho?.status === "synced" ? "In Zoho, not emailed" : "Not in Zoho"}</span>
            {i.pdfDocumentId ? <a className="ffa-small" href={`/v1/console/shipments/${i.shipmentId}/documents/${i.pdfDocumentId}`} target="_blank" rel="noopener noreferrer">Invoice PDF</a> : null}
          </span>
        ),
    },
  ];
  return (
    <Section title="Invoices" description="Issued at booking when auto-invoice is on (Admin Center > Integrations > Zoho Books), otherwise when the shipment is delivered. Emailed from Zoho Books; the PDF is the one the customer sees in their portal." flush>
      <DataTable
        caption="Invoices"
        columns={columns}
        rows={q.data?.invoices ?? []}
        rowKey={(i) => i.id}
        loading={q.isPending}
        error={q.isError ? { title: "Couldn't load invoices", onRetry: q.refetch ? () => void q.refetch!() : undefined } : null}
        empty="No invoices yet."
        rowTone={(i) => (i.status === "overdue" ? "danger" : null)}
        maxHeight="none"
      />
    </Section>
  );
}
